import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bot,
  Check,
  ChevronDown,
  CircleAlert,
  ClipboardCheck,
  Clock3,
  Database,
  FolderOpen,
  Files,
  LoaderCircle,
  MessageSquarePlus,
  MonitorCog,
  Pause,
  Pencil,
  Play,
  Route,
  Send,
  Settings2,
  Square,
  Sparkles,
  UsersRound,
  X,
} from "lucide-react";
import { api, subscribeToConversation } from "../api";
import type {
  AgentRecord,
  CliJobRecord,
  CliProvider,
  ConversationDetail,
  ConversationMode,
  ConversationSummary,
  TeamPlanStatus,
  TurnStatus,
  WorkerRecord,
} from "../../shared/types";

const cliProviders = new Set(["codex-cli", "claude-cli", "grok-cli", "hermes-cli"]);

const providerLabel: Record<CliProvider, string> = {
  "codex-cli": "Codex CLI",
  "claude-cli": "Claude CLI",
  "grok-cli": "Grok CLI",
  "hermes-cli": "Hermes",
};

const emptyEditor = {
  title: "",
  mode: "parallel" as ConversationMode,
  workspacePath: "",
  agentIds: [] as string[],
  leadAgentId: null as string | null,
};

function shortTime(value: string) {
  return new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function jobStatusLabel(job: CliJobRecord) {
  if (job.status === "queued") return "na fila";
  if (job.status === "claimed") return "preparando";
  if (job.status === "running") return job.round === 2 ? "criticando" : job.round === 3 ? "sintetizando" : "respondendo";
  return job.status;
}

function turnStatusLabel(status: TurnStatus, round: number) {
  if (status === "paused") return `Pausado · rodada ${round}`;
  if (status === "critiquing") return `Crítica · rodada ${round}`;
  if (status === "synthesizing") return `Síntese · rodada ${round}`;
  if (status === "queued") return "Na fila";
  return `Respondendo · rodada ${round}`;
}

function capabilityFor(agent: AgentRecord, workers: WorkerRecord[]) {
  return capabilityForProvider(agent.provider as CliProvider, workers);
}

function capabilityForProvider(provider: CliProvider, workers: WorkerRecord[]) {
  const online = workers.filter((worker) => worker.status === "online");
  for (const worker of online) {
    const capability = worker.capabilities[provider];
    if (capability) return { capability, worker };
  }
  for (const worker of workers) {
    const capability = worker.capabilities[provider];
    if (capability) return { capability, worker };
  }
  return null;
}

function teamPlanStatusLabel(status: TeamPlanStatus) {
  if (status === "draft") return "Aguardando aprovação";
  if (status === "approved") return "Equipe aprovada";
  if (status === "rejected") return "Composição rejeitada";
  return "Plano substituído";
}

function contextMetadata(value: unknown) {
  if (!value || typeof value !== "object") return null;
  const context = value as Record<string, unknown>;
  const files = Array.isArray(context.files) ? context.files : [];
  return {
    filesDetected: Number(context.filesDetected ?? 0),
    filesIncluded: Number(context.filesIncluded ?? 0),
    totalChars: Number(context.totalChars ?? 0),
    truncated: context.truncated === true,
    degraded: context.degraded === true,
    fileNames: files
      .map((file) => file && typeof file === "object" ? String((file as Record<string, unknown>).path ?? "") : "")
      .filter(Boolean),
    warnings: Array.isArray(context.warnings) ? context.warnings.map(String) : [],
  };
}

export function ChatView() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [workers, setWorkers] = useState<WorkerRecord[]>([]);
  const [body, setBody] = useState("");
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [turnMode, setTurnMode] = useState<ConversationMode>("parallel");
  const [sending, setSending] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editor, setEditor] = useState({ ...emptyEditor });
  const [savingEditor, setSavingEditor] = useState(false);
  const [mobileRoomsOpen, setMobileRoomsOpen] = useState(false);
  const [controlAction, setControlAction] = useState<"pause" | "resume" | "cancel" | null>(null);
  const [planAction, setPlanAction] = useState<"approved" | "rejected" | null>(null);
  const [notice, setNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const reloadTimerRef = useRef<number | null>(null);

  const loadConversations = useCallback(async () => {
    const result = await api.conversations();
    setConversations(result.conversations);
    setSelectedId((current) => current && result.conversations.some((item) => item.id === current)
      ? current
      : result.conversations[0]?.id ?? null);
  }, []);

  const loadWorkers = useCallback(async () => {
    const result = await api.workers();
    setWorkers(result.workers);
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const result = await api.conversation(id);
    setDetail(result.conversation);
  }, []);

  useEffect(() => {
    void Promise.all([
      loadConversations(),
      loadWorkers(),
      api.agents().then((result) => setAgents(result.agents)),
    ]);
    const timer = window.setInterval(() => void loadWorkers(), 15_000);
    return () => window.clearInterval(timer);
  }, [loadConversations, loadWorkers]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
    return subscribeToConversation(selectedId, () => {
      if (reloadTimerRef.current) window.clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = window.setTimeout(() => {
        void Promise.all([loadDetail(selectedId), loadConversations(), loadWorkers()]);
      }, 140);
    });
  }, [selectedId, loadDetail, loadConversations, loadWorkers]);

  const detailAgentIdsKey = detail?.agentIds.join("|") ?? "";

  useEffect(() => {
    if (!detail) return;
    setSelectedAgentIds(detail.agentIds);
    setTurnMode(detail.mode);
  }, [detail?.id, detailAgentIdsKey, detail?.mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [detail?.messages.length, detail?.jobs.length]);

  const activeJobs = useMemo(
    () => detail?.jobs.filter((job) => ["queued", "claimed", "running"].includes(job.status)).reverse() ?? [],
    [detail?.jobs],
  );
  const cliAgents = useMemo(() => agents.filter((agent) => cliProviders.has(agent.provider)), [agents]);
  const activeTurn = detail?.activeTurn ?? null;
  const turnPaused = activeTurn?.status === "paused";
  const teamPlan = detail?.teamPlan ?? null;

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 3_500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  function openCreate() {
    setEditingId(null);
    setEditor({ ...emptyEditor, title: "Novo caso", mode: "parallel" });
    setEditorOpen(true);
  }

  function openEdit() {
    if (!detail) return;
    setEditingId(detail.id);
    setEditor({
      title: detail.title,
      mode: detail.mode,
      workspacePath: detail.workspacePath,
      agentIds: detail.agentIds,
      leadAgentId: detail.leadAgentId,
    });
    setEditorOpen(true);
  }

  async function saveEditor() {
    setSavingEditor(true);
    try {
      const result = editingId
        ? await api.updateConversation(editingId, editor)
        : await api.createConversation(editor);
      await loadConversations();
      setSelectedId(result.conversation.id);
      setEditorOpen(false);
    } finally {
      setSavingEditor(false);
    }
  }

  async function sendMessage() {
    if (!detail || !body.trim() || sending || detail.activeTurn) return;
    setSending(true);
    try {
      await api.sendConversationMessage(detail.id, {
        body: body.trim(),
        agentIds: selectedAgentIds,
        mode: turnMode,
        leadAgentId: detail.leadAgentId,
      });
      setBody("");
      await Promise.all([loadDetail(detail.id), loadConversations()]);
    } catch (error) {
      setNotice({ type: "error", message: error instanceof Error ? error.message : "Não foi possível enviar." });
    } finally {
      setSending(false);
    }
  }

  async function controlTurn(action: "pause" | "resume" | "cancel") {
    if (!detail || !detail.activeTurn || controlAction) return;
    if (action === "cancel" && !window.confirm("Cancelar este turno? As respostas já concluídas serão preservadas.")) {
      return;
    }
    setControlAction(action);
    try {
      if (action === "pause") await api.pauseConversation(detail.id);
      else if (action === "resume") await api.resumeConversation(detail.id);
      else await api.cancelConversation(detail.id);
      await Promise.all([loadDetail(detail.id), loadConversations()]);
      setNotice({
        type: "success",
        message: action === "pause" ? "Turno pausado" : action === "resume" ? "Turno retomado" : "Turno cancelado",
      });
    } catch (error) {
      setNotice({ type: "error", message: error instanceof Error ? error.message : "Não foi possível controlar o turno." });
    } finally {
      setControlAction(null);
    }
  }

  async function decideTeam(decision: "approved" | "rejected") {
    if (!detail?.teamPlan || detail.teamPlan.status !== "draft" || planAction) return;
    if (decision === "rejected" && !window.confirm("Rejeitar esta composição? Os agentes continuarão inativos.")) {
      return;
    }
    setPlanAction(decision);
    try {
      await api.resolveTeamPlan(detail.id, detail.teamPlan.id, decision);
      await Promise.all([loadDetail(detail.id), loadConversations()]);
      setNotice({
        type: "success",
        message: decision === "approved" ? "Equipe aprovada e ativada" : "Composição rejeitada",
      });
    } catch (error) {
      setNotice({ type: "error", message: error instanceof Error ? error.message : "Não foi possível decidir a equipe." });
    } finally {
      setPlanAction(null);
    }
  }

  function toggleRecipient(id: string) {
    setSelectedAgentIds((current) => current.includes(id)
      ? current.filter((agentId) => agentId !== id)
      : [...current, id]);
  }

  return (
    <main className="chat-workspace">
      <aside className={`chat-rooms ${mobileRoomsOpen ? "is-open" : ""}`}>
        <div className="chat-rooms__header">
          <div><span>Mesa</span><strong>Conversas</strong></div>
          <button className="icon-button" aria-label="Nova conversa" title="Nova conversa" onClick={openCreate}><MessageSquarePlus size={17} /></button>
        </div>
        <div className="chat-rooms__list">
          {conversations.map((conversation) => (
            <button
              className={`room-row ${selectedId === conversation.id ? "is-active" : ""} ${conversation.activeTurn?.status === "paused" ? "is-paused" : ""}`}
              key={conversation.id}
              onClick={() => {
                setSelectedId(conversation.id);
                setMobileRoomsOpen(false);
              }}
            >
              <span className={`room-row__mode room-row__mode--${conversation.mode}`}><UsersRound size={14} /></span>
              <span><strong>{conversation.title}</strong><small>{conversation.lastMessage || "Sem mensagens"}</small></span>
              {conversation.teamPlan?.status === "draft"
                ? <em className="is-review" title="Equipe aguardando aprovação"><ClipboardCheck size={10} /></em>
                : conversation.activeTurn?.status === "paused"
                ? <em className="is-paused" title="Turno pausado"><Pause size={10} /></em>
                : conversation.pendingJobs > 0 && <em>{conversation.pendingJobs}</em>}
            </button>
          ))}
        </div>
      </aside>

      {detail ? (
        <section className="chat-thread">
          <header className="chat-thread__header">
            <button className="icon-button chat-mobile-rooms" aria-label="Abrir conversas" onClick={() => setMobileRoomsOpen(true)}><ChevronDown size={17} /></button>
            <div className="chat-thread__title">
              <strong>{detail.title}</strong>
              <span>
                <FolderOpen size={12} />
                {detail.workspacePath || "Sem pasta vinculada"}
                {detail.contextVersion && <b>Contexto v{detail.contextVersion.version}</b>}
              </span>
            </div>
            <span className={`mode-badge mode-badge--${detail.mode}`}>{detail.mode === "debate" ? <Sparkles size={13} /> : <UsersRound size={13} />}{detail.mode === "debate" ? "Debate" : "Paralelo"}</span>
            {activeTurn && (
              <div className="chat-turn-controls">
                <span className={`turn-status ${turnPaused ? "is-paused" : ""}`}>
                  {turnPaused ? <Pause size={12} /> : <LoaderCircle className="spin" size={12} />}
                  {turnStatusLabel(activeTurn.status, activeTurn.currentRound)}
                </span>
                {turnPaused ? (
                  <button className="icon-button" aria-label="Retomar turno" title="Retomar turno" onClick={() => void controlTurn("resume")} disabled={Boolean(controlAction)}>
                    {controlAction === "resume" ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />}
                  </button>
                ) : (
                  <button className="icon-button" aria-label="Pausar turno" title="Pausar após os trabalhos atuais" onClick={() => void controlTurn("pause")} disabled={Boolean(controlAction)}>
                    {controlAction === "pause" ? <LoaderCircle className="spin" size={16} /> : <Pause size={16} />}
                  </button>
                )}
                <button className="icon-button chat-cancel-turn" aria-label="Cancelar turno" title="Cancelar turno" onClick={() => void controlTurn("cancel")} disabled={Boolean(controlAction)}>
                  {controlAction === "cancel" ? <LoaderCircle className="spin" size={16} /> : <Square size={14} />}
                </button>
              </div>
            )}
            <button className="icon-button" aria-label="Configurar conversa" title="Configurar conversa" onClick={openEdit}><Settings2 size={16} /></button>
          </header>

          <div className="chat-messages">
            {detail.messages.map((message) => {
              const provider = message.senderAgentId
                ? detail.agents.find((agent) => agent.id === message.senderAgentId)?.provider
                : null;
              const round = Number(message.metadata.round ?? 0);
              const context = contextMetadata(message.metadata.context);
              return (
                <article className={`chat-entry chat-entry--${message.senderType} ${message.status === "error" ? "is-error" : ""}`} key={message.id}>
                  <div className={`chat-entry__avatar ${provider ? `provider-${provider}` : ""}`}>
                    {message.senderType === "user" ? "J" : message.senderType === "system" ? <Sparkles size={15} /> : <Bot size={15} />}
                  </div>
                  <div className="chat-entry__body">
                    <header><strong>{message.senderName}</strong>{provider && <span>{providerLabel[provider as CliProvider]}</span>}{round > 0 && <span>Rodada {round}</span>}<time>{shortTime(message.createdAt)}</time></header>
                    <div className="chat-entry__content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
                    </div>
                    {context && (
                      <div
                        className={`chat-entry__context ${context.filesIncluded === 0 ? "is-empty" : ""}`}
                        title={[...context.fileNames, ...context.warnings].join("\n")}
                      >
                        <Files size={12} />
                        {context.degraded
                          ? <strong>sem documentos — leitura falhou</strong>
                          : <span>{context.filesIncluded} {context.filesIncluded === 1 ? "arquivo" : "arquivos"} do caso</span>}
                        {context.totalChars > 0 && <span>{new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(context.totalChars)} caracteres</span>}
                        {context.truncated && <span>contexto limitado</span>}
                        {context.filesDetected > context.filesIncluded && <span>{context.filesDetected} detectados</span>}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
            {activeJobs.length > 0 && (
              <div className="active-agent-strip">
                {activeJobs.map((job) => (
                  <span key={job.id}>
                    {turnPaused && job.status !== "running" ? <Pause size={13} /> : <LoaderCircle className="spin" size={13} />}
                    {job.agentName ?? providerLabel[job.provider]} {turnPaused && job.status !== "running" ? "aguardando" : jobStatusLabel(job)}
                  </span>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-composer">
            {teamPlan?.status === "draft" ? (
              <div className="team-approval-gate">
                <span className="team-approval-gate__icon"><ClipboardCheck size={17} /></span>
                <div><strong>Equipe criada, aguardando sua decisão</strong><small>Os agentes permanecem inativos e ainda não receberam os documentos do caso.</small></div>
                <button className="button button--secondary" onClick={() => void decideTeam("rejected")} disabled={Boolean(planAction)}>
                  {planAction === "rejected" ? <LoaderCircle className="spin" size={14} /> : <X size={14} />}Rejeitar
                </button>
                <button className="button button--primary" onClick={() => void decideTeam("approved")} disabled={Boolean(planAction)}>
                  {planAction === "approved" ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />}Aprovar equipe
                </button>
              </div>
            ) : teamPlan?.status === "rejected" ? (
              <div className="team-approval-gate is-rejected">
                <span className="team-approval-gate__icon"><X size={17} /></span>
                <div><strong>Composição rejeitada</strong><small>O orquestrador precisa criar uma nova equipe antes da execução.</small></div>
              </div>
            ) : detail.agents.length === 0 ? (
              <div className="team-approval-gate is-waiting">
                <span className="team-approval-gate__icon"><Route size={17} /></span>
                <div><strong>Aguardando briefing</strong><small>O Codex definirá os papéis e os modelos antes de ativar qualquer agente.</small></div>
              </div>
            ) : (
              <>
                <div className="recipient-row">
                  <span>Para</span>
                  <button
                    className={selectedAgentIds.length === detail.agentIds.length ? "is-selected" : ""}
                    onClick={() => setSelectedAgentIds(detail.agentIds)}
                  >
                    @todos
                  </button>
                  {detail.agents.map((agent) => (
                    <button className={selectedAgentIds.includes(agent.id) ? "is-selected" : ""} key={agent.id} onClick={() => toggleRecipient(agent.id)}>
                      @{agent.handle}
                    </button>
                  ))}
                </div>
                <div className="composer-box">
                  <textarea
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        void sendMessage();
                      }
                    }}
                    rows={4}
                    placeholder="Escreva para a equipe ou mencione um agente específico..."
                  />
                  <div className="composer-actions">
                    <div className="composer-mode" aria-label="Modo do turno">
                      <button title="Cada agente responde uma vez" className={turnMode === "parallel" ? "is-active" : ""} onClick={() => setTurnMode("parallel")}><UsersRound size={13} />Paralelo</button>
                      <button title="Resposta, crítica cruzada e síntese" className={turnMode === "debate" ? "is-active" : ""} onClick={() => setTurnMode("debate")}><Sparkles size={13} />Debate</button>
                    </div>
                    <button className="button button--primary composer-send" onClick={() => void sendMessage()} disabled={!body.trim() || selectedAgentIds.length === 0 || sending || Boolean(activeTurn)}>
                      {sending ? <LoaderCircle className="spin" size={15} /> : <Send size={15} />}Enviar
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      ) : (
        <section className="chat-empty"><MessageSquarePlus /><h1>Crie uma mesa</h1><p>Escolha os CLIs que trabalharão juntos.</p><button className="button button--primary" onClick={openCreate}><MessageSquarePlus size={15} />Nova conversa</button></section>
      )}

      <aside className="chat-team">
        <div className="chat-team__header">
          {teamPlan ? <Route size={16} /> : <MonitorCog size={16} />}
          <div>
            <strong>{teamPlan ? "Equipe da tarefa" : "Equipe conectada"}</strong>
            <span>{teamPlan ? teamPlanStatusLabel(teamPlan.status) : workers.some((worker) => worker.status === "online") ? "Worker local online" : "Worker local offline"}</span>
          </div>
          {teamPlan && <b className={`plan-version plan-version--${teamPlan.status}`}>v{teamPlan.version}</b>}
        </div>
        <div className="team-list">
          {detail?.contextVersion && (
            <div className="case-context-summary" title={detail.contextVersion.summary}>
              <Database size={14} />
              <div><strong>Contexto v{detail.contextVersion.version}</strong><small>{detail.contextVersion.summary}</small></div>
            </div>
          )}
          {teamPlan ? teamPlan.members.map((member) => {
            const connection = capabilityForProvider(member.provider, workers);
            const ready = teamPlan.status === "approved" && connection?.worker.status === "online" && connection.capability.ready;
            const waitingAuth = connection?.capability.installed && !connection.capability.authenticated;
            const title = [
              member.rationale,
              `Entrega: ${member.outputContract}`,
              member.contextScope.length > 0 ? `Contexto: ${member.contextScope.join(", ")}` : "",
              member.fallback ? `Fallback proposto: ${providerLabel[member.fallback.provider]} · ${member.fallback.model}. ${member.fallback.reason}` : "Sem substituição automática.",
            ].filter(Boolean).join("\n");
            return (
              <div className={`team-member team-member--planned is-${teamPlan.status}`} key={member.agentId} title={title}>
                <span className={`team-member__icon provider-${member.provider}`}><Bot size={15} /></span>
                <div>
                  <strong>{member.name}</strong>
                  <small>{member.model} · {member.role}</small>
                  <p>{member.rationale}</p>
                </div>
                <span className={`team-state ${ready ? "is-ready" : waitingAuth ? "is-warning" : teamPlan.status === "draft" ? "is-planned" : ""}`} title={connection?.capability.detail}>
                  {ready ? <Check size={13} /> : waitingAuth ? <CircleAlert size={13} /> : teamPlan.status === "rejected" ? <X size={13} /> : <Clock3 size={13} />}
                </span>
              </div>
            );
          }) : detail?.agents.map((agent) => {
              const connection = capabilityFor(agent, workers);
              const ready = connection?.worker.status === "online" && connection.capability.ready;
              const waitingAuth = connection?.capability.installed && !connection.capability.authenticated;
              return (
                <div className="team-member" key={agent.id}>
                  <span className={`team-member__icon provider-${agent.provider}`}><Bot size={15} /></span>
                  <div><strong>{agent.name}</strong><small>{providerLabel[agent.provider as CliProvider]} · @{agent.handle}</small></div>
                  <span className={`team-state ${ready ? "is-ready" : waitingAuth ? "is-warning" : ""}`} title={connection?.capability.detail}>
                    {ready ? <Check size={13} /> : waitingAuth ? <CircleAlert size={13} /> : <Clock3 size={13} />}
                  </span>
                </div>
              );
            })}
        </div>
        {teamPlan?.status === "draft" ? (
          <div className="team-plan-actions">
            <button className="button button--secondary" onClick={() => void decideTeam("rejected")} disabled={Boolean(planAction)} title="Rejeitar composição"><X size={14} />Rejeitar</button>
            <button className="button button--primary" onClick={() => void decideTeam("approved")} disabled={Boolean(planAction)}><Check size={14} />Aprovar</button>
          </div>
        ) : (
          <div className="team-legend"><span><i className="is-ready" />Pronto</span><span><i className="is-warning" />Autenticação</span><span><i />Offline</span></div>
        )}
      </aside>

      {editorOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setEditorOpen(false)}>
          <div className="modal conversation-modal" role="dialog" aria-modal="true" aria-labelledby="conversation-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">Mesa multiagente</p><h2 id="conversation-title">{editingId ? "Configurar conversa" : "Nova conversa"}</h2></div><button className="icon-button" aria-label="Fechar" onClick={() => setEditorOpen(false)}><X size={17} /></button></div>
            <div className="form-grid">
              <label className="form-grid__wide"><span>Nome</span><input value={editor.title} onChange={(event) => setEditor({ ...editor, title: event.target.value })} /></label>
              <label className="form-grid__wide"><span>Pasta do caso</span><input value={editor.workspacePath} onChange={(event) => setEditor({ ...editor, workspacePath: event.target.value })} placeholder="Caminho local ou link da pasta no Google Drive" /></label>
              <div className="form-grid__wide"><span className="field-label">Modo padrão</span><div className="mode-selector"><button className={editor.mode === "parallel" ? "is-active" : ""} onClick={() => setEditor({ ...editor, mode: "parallel" })}><UsersRound size={15} /><span><strong>Paralelo</strong><small>Cada CLI responde uma vez</small></span></button><button className={editor.mode === "debate" ? "is-active" : ""} onClick={() => setEditor({ ...editor, mode: "debate" })}><Sparkles size={15} /><span><strong>Debate</strong><small>Resposta, crítica e síntese</small></span></button></div></div>
              <div className="form-grid__wide"><span className="field-label">Equipe inicial</span><div className="agent-checklist">{cliAgents.map((agent) => <label key={agent.id}><input type="checkbox" checked={editor.agentIds.includes(agent.id)} onChange={() => setEditor({ ...editor, agentIds: editor.agentIds.includes(agent.id) ? editor.agentIds.filter((id) => id !== agent.id) : [...editor.agentIds, agent.id] })} /><span className={`provider-${agent.provider}`}><Bot size={14} /></span><div><strong>{agent.name}</strong><small>@{agent.handle} · {providerLabel[agent.provider as CliProvider]}</small></div></label>)}</div></div>
              <label className="form-grid__wide"><span>Agente líder da síntese</span><select value={editor.leadAgentId ?? ""} onChange={(event) => setEditor({ ...editor, leadAgentId: event.target.value || null })} disabled={editor.agentIds.length === 0}><option value="">Definido após o briefing</option>{cliAgents.filter((agent) => editor.agentIds.includes(agent.id)).map((agent) => <option value={agent.id} key={agent.id}>{agent.name}</option>)}</select></label>
            </div>
            <div className="modal-actions"><button className="button button--secondary" onClick={() => setEditorOpen(false)}>Cancelar</button><button className="button button--primary" onClick={() => void saveEditor()} disabled={!editor.title.trim() || savingEditor}>{savingEditor ? <LoaderCircle className="spin" size={15} /> : editingId ? <Pencil size={15} /> : <MessageSquarePlus size={15} />}{editingId ? "Salvar" : "Criar caso"}</button></div>
          </div>
        </div>
      )}
      {notice && <div className={`toast ${notice.type === "error" ? "is-error" : ""}`}>{notice.type === "error" ? <CircleAlert size={15} /> : <Check size={15} />}{notice.message}</div>}
    </main>
  );
}
